import { useEffect, useState } from "react";
import { BellRing, X } from "lucide-react";
import { desktopNotifySupported, showDesktopNotification } from "../utils/desktopNotify";

// Our own "turn on alerts" card, shown before the browser's permission
// prompt. A "Block" on the browser's prompt is permanent (it won't ask
// again), so the browser is asked only when the doctor chooses "Turn on
// alerts" here. "Not now" hides this card for a day and leaves the
// browser untouched, so it can still be asked later.
const SNOOZE_KEY = "desktopAlertsPromptSnoozedUntil";
const SNOOZE_MS = 24 * 60 * 60 * 1000;

const snoozed = () => {
  try { return Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now(); } catch (e) { return false; }
};

export default function DesktopAlertsPrompt() {
  const [visible, setVisible] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (desktopNotifySupported() && Notification.permission === "default" && !snoozed()) setVisible(true);
  }, []);

  if (!visible) return null;

  const notNow = () => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)); } catch (e) { /* private mode */ }
    setVisible(false);
  };
  const turnOn = async () => {
    try {
      const result = await Notification.requestPermission();
      if (result === "granted") {
        showDesktopNotification({ title: "Alerts are on", body: "You'll be told here when a patient is waiting in a call.", tag: "alerts-on" });
        setVisible(false);
      } else if (result === "denied") {
        setDenied(true);
      } else {
        notNow();  // closed the browser prompt without choosing
      }
    } catch (e) {
      setVisible(false);
    }
  };

  return (
    <div role="dialog" aria-label="Turn on alerts" className="fixed top-20 right-4 z-[65] w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-primary-200 bg-white p-4 shadow-2xl">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <BellRing size={18} />
        </span>
        <div className="min-w-0 flex-1">
          {denied ? (
            <>
              <p className="text-sm font-bold text-secondary-900">Alerts are blocked in this browser</p>
              <p className="mt-1 text-xs text-secondary-600">
                To turn them on, click the lock icon next to the web address, set Notifications to Allow, then reload the page.
              </p>
              <button type="button" onClick={() => setVisible(false)} className="mt-3 rounded-lg border border-secondary-200 px-3 py-1.5 text-xs font-semibold text-secondary-700 hover:bg-secondary-50">
                OK
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-bold text-secondary-900">Get alerted when a patient is waiting</p>
              <p className="mt-1 text-xs text-secondary-600">
                We'll show a notification on your computer when a patient joins an online session before you, even if this window is minimised.
              </p>
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={turnOn} className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-700">
                  Turn on alerts
                </button>
                <button type="button" onClick={notNow} className="rounded-lg border border-secondary-200 px-3 py-1.5 text-xs font-semibold text-secondary-700 hover:bg-secondary-50">
                  Not now
                </button>
              </div>
              <p className="mt-2 text-[11px] text-secondary-400">Your browser will then ask you to confirm. Choose Allow.</p>
            </>
          )}
        </div>
        <button type="button" onClick={denied ? () => setVisible(false) : notNow} aria-label="Close" className="rounded-full p-1 text-secondary-400 hover:bg-secondary-100">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
